(() => {
  const INTRO_KEY = "crystal-sky:intro-seen:v1";
  const LEGACY_INTRO_KEY = "diana-lume:crystal-sky";
  const VIEW_KEY = "crystal-sky:view:v1";
  const LEGACY_VIEW_KEY = "diana-lume:sky-view";
  const PUBLIC_VIEWS = new Set(["constellation"]);
  const runtimes = new WeakMap();

  const normalizeView = (value) => PUBLIC_VIEWS.has(value) ? value : "constellation";
  const normalizeRootPath = (value) => {
    const path = typeof value === "string" && value.startsWith("/") ? value : "/";
    return path.replace(/\/+$/, "") || "/";
  };

  const storageIsWritable = (storage) => {
    if (!storage) return false;
    const probeKey = "crystal-sky:storage-probe";
    try {
      storage.setItem(probeKey, "1");
      storage.removeItem(probeKey);
      return true;
    } catch (_error) {
      return false;
    }
  };

  const safeGet = (storage, key) => {
    try {
      return storage?.getItem(key) ?? null;
    } catch (_error) {
      return null;
    }
  };

  const safeSet = (storage, key, value) => {
    try {
      storage?.setItem(key, value);
    } catch (_error) {
      // Navigation remains an ordinary browser action when storage is blocked.
    }
  };

  const markSeen = (storage) => safeSet(storage, INTRO_KEY, "seen");

  const isCrawler = (userAgent) =>
    /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|headless|lighthouse/i.test(userAgent || "");

  const runGate = (documentObject, dependencies) => {
    const config = documentObject.querySelector("[data-diana-entry-gate]");
    if (!config) return false;
    // The production homepage now owns the complete Crystal Sky experience.
    // Never replace it with the legacy secondary Page route.
    if (documentObject.querySelector("diana-react-island[data-diana-island='crystal-sky']")) return false;
    const { location, navigate, storage, userAgent = "" } = dependencies;
    if (
      config.dataset.enabled !== "true"
      || config.dataset.designMode === "true"
      || !config.dataset.skyUrl
      || normalizeRootPath(location.pathname) !== normalizeRootPath(config.dataset.rootUrl)
      || location.search !== ""
      || location.hash !== ""
      || isCrawler(userAgent)
      || !storageIsWritable(storage)
    ) return false;

    if (safeGet(storage, INTRO_KEY) === "seen") return false;
    if (safeGet(storage, LEGACY_INTRO_KEY) === "complete") {
      markSeen(storage);
      return false;
    }

    // The previous homepage module still reads this key. Marking it prevents
    // its compatibility anchor from racing the Page redirect or looping home.
    safeSet(storage, LEGACY_INTRO_KEY, "complete");
    navigate(config.dataset.skyUrl);
    return true;
  };

  const syncSkyView = (documentObject, location, history, storage) => {
    const propsNode = documentObject.querySelector(
      "diana-react-island[data-diana-island='crystal-sky'] [data-diana-island-props]",
    );
    if (!propsNode) return "constellation";

    let props;
    try {
      props = JSON.parse(propsNode.textContent || "");
    } catch (_error) {
      return "constellation";
    }

    const query = new URLSearchParams(location.search || "");
    const queryScene = query.get("scene");
    const legacyQueryView = PUBLIC_VIEWS.has(query.get("view")) ? query.get("view") : null;
    const storedView = safeGet(storage, VIEW_KEY) ?? safeGet(storage, LEGACY_VIEW_KEY);
    const view = normalizeView(queryScene ?? legacyQueryView ?? storedView);
    props.initialView = view;
    propsNode.textContent = JSON.stringify(props);
    safeSet(storage, VIEW_KEY, view);

    // The root already renders Constellation. Retire only its legacy selectors;
    // keep attribution, preview, locale and hash context, and never add a gate.
    const atHome = normalizeRootPath(location.pathname) === normalizeRootPath(props.homeUrl);
    const legacyViews = new Set(["crystal-sky", "constellation", "voyage", "observatory"]);
    const removeView = legacyViews.has(query.get("view"));
    if (
      atHome && (removeView || query.has("scene"))
      && history
      && typeof history.replaceState === "function"
    ) {
      if (removeView) query.delete("view");
      query.delete("scene");
      const search = query.toString();
      history.replaceState(history.state, "", `${location.pathname}${search ? `?${search}` : ""}${location.hash || ""}`);
    }
    return view;
  };

  const bindNavigation = (documentObject, storage) => {
    const onClick = (event) => {
      const target = event.target instanceof documentObject.defaultView.Element
        ? event.target.closest("a")
        : null;
      if (!target) return;
      if (target.matches("[data-sky-skip], [data-story-entry]")) markSeen(storage);
      if (target.matches("[data-sky-view]")) {
        safeSet(storage, VIEW_KEY, normalizeView(target.dataset.skyView));
      }
    };
    documentObject.addEventListener("click", onClick, true);
    return () => documentObject.removeEventListener("click", onClick, true);
  };

  const bindEditor = (documentObject) => {
    const onBlockSelect = (event) => {
      const eventTarget = event.target instanceof documentObject.defaultView.Element
        ? event.target
        : null;
      const blockId = event.detail?.blockId || "";
      const explicitKey = event.detail?.storyKey || "";
      const blockTarget = eventTarget?.closest("[data-sky-block-id]")
        || [...documentObject.querySelectorAll("[data-sky-block-id]")]
          .find((candidate) => candidate.dataset.skyBlockId === blockId);
      const storyTarget = blockTarget?.querySelector("[data-sky-story-key]")
        || eventTarget?.closest("[data-sky-story-key]")
        || [...documentObject.querySelectorAll("[data-sky-story-key]")]
          .find((candidate) => candidate.dataset.skyStoryKey === explicitKey);
      const host = storyTarget?.closest("diana-react-island[data-diana-island='crystal-sky']");
      if (!host || !storyTarget.dataset.skyStoryKey) return;
      host.dispatchEvent(new documentObject.defaultView.CustomEvent("diana:sky-story-select", {
        bubbles: true,
        detail: {
          storyKey: storyTarget.dataset.skyStoryKey,
          sectionId: host.dataset.sectionId || "",
        },
      }));
    };
    documentObject.addEventListener("shopify:block:select", onBlockSelect);
    return () => documentObject.removeEventListener("shopify:block:select", onBlockSelect);
  };

  const mountDocument = (documentObject, dependencies) => {
    const existing = runtimes.get(documentObject);
    if (existing) return existing.cleanup;

    syncSkyView(
      documentObject,
      dependencies.location,
      dependencies.history,
      dependencies.storage,
    );
    const cleanNavigation = bindNavigation(documentObject, dependencies.storage);
    const cleanEditor = bindEditor(documentObject);
    runGate(documentObject, dependencies);

    let active = true;
    const cleanup = () => {
      if (!active) return;
      active = false;
      cleanNavigation();
      cleanEditor();
      runtimes.delete(documentObject);
    };
    runtimes.set(documentObject, { cleanup });
    return cleanup;
  };

  const api = Object.freeze({
    bindNavigation,
    markSeen,
    mountDocument,
    normalizeView,
    runGate,
    syncSkyView,
  });
  globalThis.DianaEntryGate = api;

  if (
    typeof document !== "undefined"
    && typeof window !== "undefined"
    && document.currentScript
  ) {
    let storage = null;
    try {
      storage = window.localStorage;
    } catch (_error) {
      storage = null;
    }
    mountDocument(document, {
      storage,
      location: window.location,
      history: window.history,
      navigate: (url) => window.location.replace(url),
      userAgent: window.navigator?.userAgent || "",
    });
  }
})();
